import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchStatus, readCachedStatus } from "../utils/flowGuards";

function hasPaidMembership(status) {
  if (status?.membershipActive) return true;
  const entitlements = status?.entitlements || {};
  if (entitlements.membership_active) return true;
  if (entitlements.dashboard_access === "full") return true;
  if (entitlements.chat_access && entitlements.chat_requires_membership === false) return true;
  const tier = String(entitlements.internal_tier || status?.tier || "").toLowerCase();
  return tier === "core" || tier === "premium";
}

/**
 * Blocks Dashboard, Chat, and money pages unless a paid membership is already active.
 * Trip buyers go back to Trips. This does not start a membership checkout.
 */
export default function MembershipRequired({ surface = "membership", children }) {
  const navigate = useNavigate();
  const [allowed, setAllowed] = useState(() => hasPaidMembership(readCachedStatus()));

  useEffect(() => {
    let cancelled = false;
    const cachedMembershipActive = hasPaidMembership(readCachedStatus());

    const guard = async () => {
      try {
        const status = await fetchStatus({
          timeoutMs: 8000,
          retries: 1,
          useCache: true,
        });
        if (cancelled) return;
        if (hasPaidMembership(status)) {
          setAllowed(true);
          return;
        }
      } catch {
        // A recently verified member keeps navigating during a transient
        // revalidation failure; protected APIs remain the security boundary.
        if (cachedMembershipActive) {
          if (!cancelled) setAllowed(true);
          return;
        }
      }
      if (cancelled) return;
      navigate("/trips", { replace: true });
    };

    void guard();
    return () => {
      cancelled = true;
    };
  }, [navigate, surface]);

  if (!allowed) {
    const surfaceLabel = surface === "chat" ? "Chat" : surface === "dashboard" ? "Dashboard" : "membership";
    return (
      <div className="membership-required-gate" role="status" aria-live="polite">
        <div className="membership-required-gate__card">
          <span className="membership-required-gate__spinner" aria-hidden="true" />
          <strong>Checking {surfaceLabel} access</strong>
          <span>One moment while Dople confirms your membership.</span>
        </div>
      </div>
    );
  }

  return children;
}
